import "./index.css";
import "./i18n"; // Bootstrap internationalisation
import "shared/core/types/authStorage";

import { Error404, handleError } from "./UIPalette/Error404/Error404";
import { appInsights, appInsightsLogger, initialiseAppInsights, reactPlugin, sendToAppInsights, withAppInsights } from "core/monitoring/AppInsights";
import { getAppVersion, getDeviceInfo, getMobilePlatform } from "core/javascriptChannels";

import { App } from "./App";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { Log } from "oidc-client-ts";
import React from "react";
import ReactDOM from "react-dom/client";
import { UserContextProvider } from "core/state/UserContext/UserContext";
import { VersionedAuthProvider } from "core/auth/VersionedAuthProvider";
import { initialiseGTM } from "core/monitoring/GoogleAnalytics";
import reportWebVitals from "./reportWebVitals";
import { satisfies } from "semver";

const addBodyClass = (className: string) => {
  document.body.classList.add(className);
};

const fetchDeviceDetails = async () => {
  const [appVersion, deviceInfo, platform] = await Promise.all([getAppVersion(), getDeviceInfo(), getMobilePlatform()]);
  return {
    appVersion,
    deviceInfo,
    platform,
  };
};

const isMobile = !!window.flutter_inappwebview;
try {
  process.env.REACT_APP_NEW_WISDOM && addBodyClass("new-wisdom");
  fetchDeviceDetails().then(({ appVersion, deviceInfo, platform }) => {
    initialiseAppInsights({
      Platform: isMobile ? platform : "web",
      ...(isMobile && {
        AppVersion: appVersion ?? "",
        DeviceInfo: deviceInfo ?? "",
      }),
    });

    // only use localStorage for mobile clients using Auth v1
    const useLocalStorage = isMobile && !satisfies(appVersion, process.env.REACT_APP_NEW_AUTH_VERSION!);
    window.authStorage = useLocalStorage ? localStorage : sessionStorage;

    (process.env.REACT_APP_GTM_ID || process.env.REACT_APP_AU_GTM_ID) && initialiseGTM();
    Log.setLogger(appInsightsLogger);

    const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

    const AppWithTracking = withAppInsights(App);

    console.log("Initialisation of auth finished, trying to render React App");
    root.render(
      <React.StrictMode>
        <ErrorBoundary FallbackComponent={Error404} onError={handleError} onReset={() => (window.location.href = "/home")}>
          <BrowserRouter>
            <AppInsightsContext.Provider value={reactPlugin}>
              <VersionedAuthProvider appVersion={appVersion}>
                <UserContextProvider>
                  <AppWithTracking />
                </UserContextProvider>
              </VersionedAuthProvider>
            </AppInsightsContext.Provider>
          </BrowserRouter>
        </ErrorBoundary>
      </React.StrictMode>
    );
    reportWebVitals(sendToAppInsights);
  });
} catch (err: any) {
  appInsights.trackException({ exception: err, severityLevel: 3 });
  console.error(err);
}
